import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Toast notification state
 */
export interface ToastState {
  /** Toast message to display */
  message: string;
  /** Toast type for styling */
  type: 'success' | 'error' | 'info';
}

/**
 * UI state and actions
 */
interface UIState {
  // Transient state (NOT persisted)

  /** Whether to show the onboarding flow */
  showOnboarding: boolean;
  /** Current toast notification, or null if none */
  toast: ToastState | null;

  // Persisted state

  /** Last visited screen route */
  lastVisitedScreen: string;

  // Actions

  /**
   * Set whether to show the onboarding flow
   * @param show - Whether to show onboarding
   */
  setShowOnboarding: (show: boolean) => void;

  /**
   * Show a toast notification
   * @param message - Message to display
   * @param type - Toast type for styling
   */
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;

  /**
   * Hide the current toast notification
   */
  hideToast: () => void;

  /**
   * Set the last visited screen route
   * @param screen - Screen route to save
   */
  setLastVisitedScreen: (screen: string) => void;
}

/**
 * UI state store with partial AsyncStorage persistence
 *
 * Features:
 * - Persistent storage of last visited screen
 * - Transient onboarding and toast state
 * - Toast notifications management
 *
 * Note: Only lastVisitedScreen is persisted. Onboarding and toast state
 * are intentionally transient and reset on app restart.
 */
export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Transient state
      showOnboarding: true,
      toast: null,

      // Persisted state
      lastVisitedScreen: '/',

      // Actions
      setShowOnboarding: (show) => {
        set({ showOnboarding: show });
      },

      showToast: (message, type) => {
        set({ toast: { message, type } });
      },

      hideToast: () => {
        set({ toast: null });
      },

      setLastVisitedScreen: (screen) => {
        set({ lastVisitedScreen: screen });
      },
    }),
    {
      name: 'drama-ui-storage',
      storage: createJSONStorage(() => AsyncStorage),
      // Only persist lastVisitedScreen
      partialize: (state) => ({
        lastVisitedScreen: state.lastVisitedScreen,
      }),
    }
  )
);
